import { useState } from "react";
import { Loader2, ShieldCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";

export function ForcePasswordChange() {
  const { user, changePassword, logout } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (next.length < 6) return setError("New password must be at least 6 characters.");
    if (next !== confirm) return setError("New password and confirmation do not match.");
    if (next === current) return setError("Choose a password different from the temporary one.");
    setSaving(true);
    const res = await changePassword(current, next);
    setSaving(false);
    if (res.ok) toast.success("Password updated — welcome aboard!");
    else setError(res.error);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#231f20] px-4" data-testid="force-password-change">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-7">
        <div className="flex items-center gap-3 mb-1">
          <span className="h-10 w-10 rounded-full bg-brand/10 flex items-center justify-center">
            <ShieldCheck className="h-5 w-5 text-brand" />
          </span>
          <div>
            <h1 className="font-heading text-xl font-bold text-slate-900">Set a new password</h1>
            <p className="text-xs text-slate-500">Signed in as {user?.email}</p>
          </div>
        </div>
        <p className="text-sm text-slate-600 mt-3 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
          You're using a temporary password issued by your administrator. Please choose a new password to continue.
        </p>

        <form onSubmit={submit} className="mt-5 space-y-3.5" data-testid="force-password-form">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Temporary password</Label>
            <PasswordInput value={current} onChange={(e) => setCurrent(e.target.value)} required
              className="h-10" placeholder="The password you just used" testid="fpc-current" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">New password</Label>
            <PasswordInput value={next} onChange={(e) => setNext(e.target.value)} required
              className="h-10" placeholder="At least 6 characters" testid="fpc-new" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-700">Confirm new password</Label>
            <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} required
              className="h-10" placeholder="Re-enter new password" testid="fpc-confirm" />
          </div>
          {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="fpc-error">{error}</p>}
          <Button type="submit" disabled={saving} className="w-full h-10 bg-brand hover:bg-brand/90 text-white gap-2" data-testid="fpc-submit">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="h-4 w-4" /> Update password</>}
          </Button>
        </form>

        <button onClick={logout} className="mt-4 text-xs text-slate-400 hover:text-slate-600 w-full text-center" data-testid="fpc-logout">
          Sign out instead
        </button>
      </div>
    </div>
  );
}
